import { action, makeObservable, observable } from "mobx";
import StorageCostAPI from "../api/StorageCostAPI";
import {
  GetStorageCostDetailParams,
  GetStorageCostParams,
  RecalculateStorageCostParams,
  RecalculateStorageCostResult,
  StorageCostDetailItem,
  StorageCostListItem,
} from "../../../models/StorageCost/StorageCost";
import { FilterValue } from "../../../models/Filters/FilterValue";
import { APIBlobResponse, APIResponse } from "../../../models/NetworkResponse";

export class StorageCostStore {
  storageCostAPI: StorageCostAPI;
  records: StorageCostListItem[];
  detailRows: StorageCostDetailItem[];
  isDetailOpen: boolean;
  isFiltersExpanded: boolean;
  customerOptions: FilterValue[];

  constructor() {
    this.storageCostAPI = new StorageCostAPI();
    this.records = [];
    this.detailRows = [];
    this.isDetailOpen = false;
    this.isFiltersExpanded = true;
    this.customerOptions = [];

    makeObservable(this, {
      records: observable,
      detailRows: observable,
      isDetailOpen: observable,
      isFiltersExpanded: observable,
      customerOptions: observable,
      getStorageCostListing: action,
      getStorageCostDetail: action,
      recalculateStorageCost: action,
      downloadStorageCostDetailReport: action,
      searchTags: action,
      setIsDetailOpen: action,
      setIsFiltersExpanded: action,
      setCustomerOptions: action,
      clearSelectedDetail: action,
      reset: action,
    });
  }

  private getRowsFromResponse = <T>(response: unknown): T[] => {
    if (Array.isArray(response)) return response as T[];
    if (response && typeof response === "object") {
      const responseObject = response as { list?: unknown; result?: unknown };
      if (Array.isArray(responseObject.list)) return responseObject.list as T[];
      if (Array.isArray(responseObject.result))
        return responseObject.result as T[];
    }
    return [];
  };

  getStorageCostListing = async (params: GetStorageCostParams) => {
    try {
      const response = await this.storageCostAPI.getStorageCostListing(params);
      const sourceRows = this.getRowsFromResponse<any>(response);
      this.records = sourceRows.map((row: any) => ({
        id: Number(row?.id) || 0,
        customerId: Number(row?.customerId ?? row?.customer_id) || 0,
        customerName: String(
          row?.customerName ?? row?.customer_name ?? "",
        ).trim(),
        inventory_label: String(
          row?.inventory_label ?? row?.inventoryLabel ?? "",
        ).trim(),
        createdDate: String(row?.createdDate ?? row?.created_date ?? "").trim(),
        isMasterInventory: Boolean(
          row?.isMasterInventory ?? row?.is_master_inventory,
        ),
        totalCost: Number(row?.totalCost ?? row?.total_cost) || 0,
      }));
    } catch (error) {
      throw error;
    }
  };

  getStorageCostDetail = async (params: GetStorageCostDetailParams) => {
    try {
      const detail = await this.storageCostAPI.getStorageCostDetail(params);
      const sourceRows = this.getRowsFromResponse<any>(detail);
      this.detailRows = sourceRows.map((row: any) => ({
        id: Number(row?.id) || undefined,
        label: String(row?.label ?? "").trim(),
        size: String(row?.size ?? "").trim(),
        pack_size: row?.pack_size ?? row?.packSize ?? "",
        box_size: String(row?.box_size ?? row?.boxSize ?? "").trim(),
        monthly_storage_cost:
          Number(row?.monthly_storage_cost ?? row?.monthlyStorageCost) || 0,
        quantity: Number(row?.quantity) || 0,
        total_cost_per_day:
          Number(row?.total_cost_per_day ?? row?.totalCostPerDay) || 0,
        date: String(row?.date ?? "").trim(),
        partner_tag: String(row?.partner_tag ?? row?.partnerTag ?? "").trim(),
      }));
      return this.detailRows;
    } catch (error) {
      throw error;
    }
  };

  recalculateStorageCost = async (
    params: RecalculateStorageCostParams,
  ): Promise<APIResponse & { result?: RecalculateStorageCostResult }> => {
    try {
      const response = await this.storageCostAPI.recalculateStorageCost(params);
      return response;
    } catch (error) {
      throw error;
    }
  };

  downloadStorageCostDetailReport = async (
    params: GetStorageCostDetailParams,
  ): Promise<APIBlobResponse> => {
    try {
      const response =
        await this.storageCostAPI.downloadStorageCostDetailReport(params);
      return response;
    } catch (error) {
      throw error;
    }
  };

  searchTags = async (search_text: string): Promise<APIResponse> => {
    try {
      const response = await this.storageCostAPI.searchTags(search_text);
      return response;
    } catch (error) {
      throw error;
    }
  };

  clearSelectedDetail = () => {
    this.detailRows = [];
  };

  setIsDetailOpen = (open: boolean) => {
    this.isDetailOpen = open;
  };

  setIsFiltersExpanded = (expanded: boolean) => {
    this.isFiltersExpanded = expanded;
  };

  setCustomerOptions = (options: FilterValue[]) => {
    this.customerOptions = options;
  };

  reset = () => {
    this.records = [];
    this.detailRows = [];
    this.isDetailOpen = false;
    this.isFiltersExpanded = true;
    this.customerOptions = [];
  };
}
